import apiBaseUrl from '../../utils/config.js';

class SuperbillService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/superbill`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders() {
    const token = this.getToken();

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  async request(endpoint, method = 'GET', body = null) {
    const options = {
      method,
      headers: this.getAuthHeaders(),
    };

    if (body !== null && body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, options);

    const text = await response.text();

    let data = null;

    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      const message =
        data?.message ||
        data?.error ||
        data ||
        `Request failed with status ${response.status}`;

      throw new Error(message);
    }

    return data;
  }

  async getByDate(date) {
    const params = new URLSearchParams({ date });
    return this.request(`/by-date?${params.toString()}`);
  }

  async getByDateRange(startDate, endDate) {
    const params = new URLSearchParams({
      startDate,
      endDate,
    });

    return this.request(`/by-date-range?${params.toString()}`);
  }

  async getByDatePaged(date, page = 0, size = 25) {
    const params = new URLSearchParams({
      date,
      page,
      size,
    });

    return this.request(`/by-date-paged?${params.toString()}`);
  }

  async getByDateRangePaged(startDate, endDate, page = 0, size = 25) {
    const params = new URLSearchParams({
      startDate,
      endDate,
      page,
      size,
    });

    return this.request(`/by-date-range-paged?${params.toString()}`);
  }

  async getByAppointment(appointmentId) {
    return this.request(`/by-appointment/${appointmentId}`);
  }

  async markBilled(superbillId, staffId) {
    return this.request(`/mark-billed/${superbillId}`, 'PATCH', {
      staffId,
    });
  }

  async markNotesCompleted(superbillId, staffId) {
    return this.request(`/mark-notes/${superbillId}`, 'PATCH', {
      staffId,
    });
  }

  async getViewPaged({
    startDate,
    endDate,
    page = 0,
    size = 25,
    patientSearch = '',
    insuranceSearch = '',
    officeId = null,
    billed = null,
    notesCompleted = null,
    sortMode = 'OFFICE_PATIENT',
  }) {
    const params = new URLSearchParams();

    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('page', page);
    params.append('size', size);

    if (patientSearch) {
      params.append('patientSearch', patientSearch);
    }

    if (insuranceSearch) {
      params.append('insuranceSearch', insuranceSearch);
    }

    if (officeId !== null && officeId !== undefined && officeId !== '') {
      params.append('officeId', officeId);
    }

    if (billed !== null && billed !== undefined && billed !== '') {
      params.append('billed', billed);
    }

    if (
      notesCompleted !== null &&
      notesCompleted !== undefined &&
      notesCompleted !== ''
    ) {
      params.append('notesCompleted', notesCompleted);
    }

    if (sortMode) {
      params.append('sortMode', sortMode);
    }

    return this.request(`/view-paged?${params.toString()}`);
  }

  async updateSuperbill(superbillId, updateData) {
    return this.request(`/update/${superbillId}`, 'PUT', updateData);
  }

  async deleteSuperbill(superbillId) {
    return this.request(`/delete/${superbillId}`, 'DELETE');
  }

  /*
   * Fast creator endpoint.
   * Loads authorization, insurance-patient defaults, office/doctor names,
   * diagnosis/procedure, existing superbill dates, and allowed type visits.
   */
  async getCreatorData(authId) {
    return this.request(`/creator-data/${authId}`);
  }

  /*
   * Gets all superbill dates already used for an authorization.
   */
  async getDatesByAuthId(authId) {
    return this.request(`/auth/${authId}/dates`);
  }

  /*
   * Checks if a superbill date already exists for an authorization.
   */
  async dateExistsForAuth(authId, date) {
    const params = new URLSearchParams({ date });

    return this.request(`/auth/${authId}/date-exists?${params.toString()}`);
  }

  /*
   * Bulk marks all currently filtered superbills as billed.
   * filterData should include:
   * startDate, endDate, patientSearch, insuranceSearch,
   * officeId, billed, notesCompleted, staffId
   */
  async bulkMarkBilled(filterData) {
    return this.request('/bulk-mark-billed', 'PATCH', filterData);
  }

  /*
   * Bulk marks all currently filtered superbills as notes completed.
   * filterData should include:
   * startDate, endDate, patientSearch, insuranceSearch,
   * officeId, billed, notesCompleted, staffId
   */
  async bulkMarkNotesCompleted(filterData) {
    return this.request('/bulk-mark-notes', 'PATCH', filterData);
  }
}

export default SuperbillService;